import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type CreateApiKeyRequest,
  type ApiKeyPermissions as GeneratedAPIKeyPermissions,
  type UpdateApiKeyRequest,
  apikeyCreateApiKeyMutation,
  apikeyGetApiKeyOptions,
  apikeyGetApiKeyQueryKey,
  apikeyListApiKeysQueryKey,
  apikeyUpdateApiKeyMutation,
} from '@column/api';
import { Button, Dropdown, Icon, Input, Textarea } from '@column/column-ui-kit';
import { Badge, Tabs, TabsList, TabsTrigger, cn } from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { ApiKeyInput, Loading, PopoverBase, RenderFields, RestrictedBanner } from '~/components';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import {
  CustomRoleBankAccountLevelOverrides,
  CustomRoleEntityLevelOverrides,
  CustomRolePlatformLevelPermissions,
} from '~/typings/API';
import { keysToCamelCase, keysToSnakeCase } from '~/util/convertKeys';

import { useDeleteAPIKeyAlert } from './_hooks/useDeleteAPIKeyAlert';
import { getErrorMessage } from './utils';

interface Params {
  id: string;
}

enum Section {
  Transfers = 'api-key-transfers-and-accounts',
  Additional = 'api-key-additional-permissions',
}

enum APIKeyPermissionPreset {
  ViewOnly = 'view-only',
  FullAccess = 'full-access',
  Custom = 'custom',
}

interface APIKeyFormValues {
  apiKeyToken?: string;
  description: string;
  name: string;
  permissions: APIKeyPermissions;
  platformId: string;
}

type APIKeySaveValues = Omit<APIKeyFormValues, 'apiKeyToken'>;

interface PermissionsPanelSnapshot {
  permissions: APIKeyPermissions;
  preset: APIKeyPermissionPreset;
}

interface APIKeyPermissions {
  bankAccountLevelOverrides?: CustomRoleBankAccountLevelOverrides[];
  entityLevelOverrides?: CustomRoleEntityLevelOverrides[];
  platformLevelPermissions: CustomRolePlatformLevelPermissions;
}

const excludedAPIKeyPermissionKeys = ['apiKeys', 'approveTransfers'] as const;
const includedAPIKeyAdditionalPermissionKeys = ['counterparties', 'loans', 'reporting'] as const;
const apiKeyVisiblePlatformPermissionKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'counterparties',
  'depositChecks',
  'entities',
  'issueChecks',
  'loans',
  'realtimeTransfers',
  'reporting',
  'stopPayments',
  'wireTransfers',
] as const satisfies readonly (keyof APIKeyPermissions['platformLevelPermissions'])[];
const apiKeyVisibleBankAccountLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisBankAccount',
  'wireTransfers',
] as const;
const apiKeyVisibleEntityLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisEntity',
  'wireTransfers',
] as const;

const apiKeyPermissionPresetOptions = [
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    label: 'Full Access',
    value: APIKeyPermissionPreset.FullAccess,
  },
  {
    icon: <Icon.Eye className="[--icon-color:var(--color-primary)]!" />,
    label: 'View Only',
    value: APIKeyPermissionPreset.ViewOnly,
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    label: 'Custom',
    value: APIKeyPermissionPreset.Custom,
  },
];
const apiKeySections = [
  { label: 'Transfers and Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];

const createAPIKeyPlatformLevelPermissions = (
  permissionLevel: 'read' | 'write',
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissions['platformLevelPermissions'] => {
  const granularPermissionLevel = permissionLevel === 'write' ? 'create-delete' : permissionLevel;

  return {
    achCredits: platformLevelPermissions?.achCredits ?? permissionLevel,
    achDebits: platformLevelPermissions?.achDebits ?? permissionLevel,
    achReturns: platformLevelPermissions?.achReturns ?? permissionLevel,
    bankAccounts: platformLevelPermissions?.bankAccounts ?? granularPermissionLevel,
    bookTransfers: platformLevelPermissions?.bookTransfers ?? permissionLevel,
    counterparties: platformLevelPermissions?.counterparties ?? permissionLevel,
    depositChecks: platformLevelPermissions?.depositChecks ?? permissionLevel,
    entities: platformLevelPermissions?.entities ?? permissionLevel,
    issueChecks: platformLevelPermissions?.issueChecks ?? permissionLevel,
    loans: platformLevelPermissions?.loans ?? permissionLevel,
    realtimeTransfers: platformLevelPermissions?.realtimeTransfers ?? permissionLevel,
    reporting: platformLevelPermissions?.reporting ?? 'read',
    stopPayments: platformLevelPermissions?.stopPayments ?? permissionLevel,
    wireTransfers: platformLevelPermissions?.wireTransfers ?? permissionLevel,
  };
};

const createDefaultAPIKeyPermissions = (): APIKeyPermissions => ({
  bankAccountLevelOverrides: [],
  entityLevelOverrides: [],
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write'),
});

const createPresetAPIKeyPermissions = (permissionLevel: 'read' | 'write', platformId?: string): APIKeyFormValues => ({
  ...defaultFormValues(platformId),
  permissions: {
    bankAccountLevelOverrides: [],
    entityLevelOverrides: [],
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions(permissionLevel),
  },
});

const defaultFormValues = (platformId?: string): APIKeyFormValues => ({
  apiKeyToken: '',
  description: '',
  name: '',
  permissions: createDefaultAPIKeyPermissions(),
  platformId: platformId ?? '',
});

const getAPIKeyPermissionPreset = (
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissionPreset => {
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) => platformLevelPermissions[permission] === 'read')
  ) {
    return APIKeyPermissionPreset.ViewOnly;
  }
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) =>
      permission === 'reporting'
        ? platformLevelPermissions[permission] === 'read'
        : platformLevelPermissions[permission] === 'write'
    )
  ) {
    return APIKeyPermissionPreset.FullAccess;
  }
  return APIKeyPermissionPreset.Custom;
};

const getAPIKeyPermissionPresetFromPermissions = (permissions: APIKeyPermissions): APIKeyPermissionPreset => {
  if ((permissions.bankAccountLevelOverrides?.length ?? 0) > 0 || (permissions.entityLevelOverrides?.length ?? 0) > 0) {
    return APIKeyPermissionPreset.Custom;
  }

  return getAPIKeyPermissionPreset(permissions.platformLevelPermissions);
};

const trimOverrides = <T extends object>(
  overrides: T[] | undefined,
  idKey: keyof T,
  visibleKeys?: readonly string[]
): T[] =>
  (overrides ?? [])
    .map(
      (override) =>
        Object.fromEntries(
          Object.entries(override as Record<string, unknown>).filter(
            ([key, value]) => key === String(idKey) || (value !== 'default' && (visibleKeys?.includes(key) ?? true))
          )
        ) as T
    )
    .filter((override) => Object.keys(override).length > 1);

const apiKeyPermissionsToForm = (permissions: APIKeyPermissions | GeneratedAPIKeyPermissions): APIKeyPermissions =>
  keysToCamelCase(permissions) as APIKeyPermissions;

const apiKeyPermissionsToRequest = (permissions: APIKeyPermissions): GeneratedAPIKeyPermissions =>
  keysToSnakeCase(permissions) as GeneratedAPIKeyPermissions;

const normalizeAPIKeyPermissions = (
  permissions?: APIKeyPermissions | GeneratedAPIKeyPermissions | null
): APIKeyPermissions => {
  const formPermissions = permissions ? apiKeyPermissionsToForm(permissions) : undefined;

  return {
    bankAccountLevelOverrides: trimOverrides(
      formPermissions?.bankAccountLevelOverrides,
      'bankAccountId',
      apiKeyVisibleBankAccountLevelOverrideKeys
    ),
    entityLevelOverrides: trimOverrides(
      formPermissions?.entityLevelOverrides,
      'entityId',
      apiKeyVisibleEntityLevelOverrideKeys
    ),
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', formPermissions?.platformLevelPermissions),
  };
};

const sanitizeAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: trimOverrides(
    permissions.bankAccountLevelOverrides,
    'bankAccountId',
    apiKeyVisibleBankAccountLevelOverrideKeys
  ),
  entityLevelOverrides: trimOverrides(
    permissions.entityLevelOverrides,
    'entityId',
    apiKeyVisibleEntityLevelOverrideKeys
  ),
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', permissions.platformLevelPermissions),
});

const getPermissionSavePayload = (
  preset: APIKeyPermissionPreset,
  permissions: APIKeyPermissions
): GeneratedAPIKeyPermissions | null =>
  preset === APIKeyPermissionPreset.FullAccess
    ? null
    : apiKeyPermissionsToRequest(sanitizeAPIKeyPermissions(permissions));

const copyAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: (permissions.bankAccountLevelOverrides ?? []).map((override) => ({ ...override })),
  entityLevelOverrides: (permissions.entityLevelOverrides ?? []).map((override) => ({ ...override })),
  platformLevelPermissions: { ...permissions.platformLevelPermissions },
});

const getAPIKeySaveValues = (data: APIKeyFormValues): APIKeySaveValues => ({
  description: data.description,
  name: data.name,
  permissions: data.permissions,
  platformId: data.platformId,
});

const serializeAPIKeySaveValues = (values: APIKeySaveValues): string => JSON.stringify(values);

const getAPIErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorRecord = error as { code?: unknown };

  if (typeof errorRecord.code === 'string') {
    return errorRecord.code;
  }

  return undefined;
};

export const PageApiKeysEdit: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const apiKeyPermissionsEnabled = useFeatureFlag(FeatureFlag.EnableAPIKeyPermissions);
  const queryClient = useQueryClient();
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const [visibleSection, setVisibleSection] = useState<Section>(Section.Transfers);
  const [isPermissionsPanelOpen, setIsPermissionsPanelOpen] = useState<boolean>(false);
  const [savedAPIKeySaveValues, setSavedAPIKeySaveValues] = useState<APIKeySaveValues | null>(null);
  const [selectedPermissionPreset, setSelectedPermissionPreset] = useState<APIKeyPermissionPreset>(
    APIKeyPermissionPreset.FullAccess
  );

  const formMethods = useForm<APIKeyFormValues>({
    defaultValues: defaultFormValues(currentPlatform?.id ?? currentUser?.defaultPlatformId),
  });
  const { control, getValues, handleSubmit, reset, setValue } = formMethods;

  const permissionsPanelSnapshot = useRef<PermissionsPanelSnapshot | null>(null);

  const formValues = useWatch({ control }) as APIKeyFormValues;
  const bankAccountLevelOverrides = useWatch({ control, name: 'permissions.bankAccountLevelOverrides' });
  const entityLevelOverrides = useWatch({ control, name: 'permissions.entityLevelOverrides' });

  const selectedMode = isSandbox ? Mode.Sandbox : Mode.Live;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId ?? '';
  const apiKeyQuery = useQuery({
    ...apikeyGetApiKeyOptions({
      path: { api_key_id: id ?? '', platform_id: platformId },
    }),
    enabled: !!id && !!platformId,
    retry: (failureCount, error) => getAPIErrorCode(error) !== 'invalid_resource_id_format' && failureCount < 3,
  });
  const createAPIKey = useMutation(apikeyCreateApiKeyMutation());
  const updateAPIKey = useMutation(apikeyUpdateApiKeyMutation());
  const openDeleteAPIKeyAlert = useDeleteAPIKeyAlert({
    platformId,
    redirectOnDelete: true,
  });

  const existingBankAccountIDs = useMemo(
    () =>
      (bankAccountLevelOverrides ?? [])
        .map((bankAccount) => bankAccount.bankAccountId)
        .filter((bankAccountId): bankAccountId is string => typeof bankAccountId === 'string'),
    [bankAccountLevelOverrides]
  );
  const existingEntityIDs = useMemo(
    () =>
      (entityLevelOverrides ?? [])
        .map((entity) => entity.entityId)
        .filter((entityId): entityId is string => typeof entityId === 'string'),
    [entityLevelOverrides]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  useEffect(() => {
    if (!apiKeyPermissionsEnabled) {
      setIsPermissionsPanelOpen(false);
      permissionsPanelSnapshot.current = null;
    }
  }, [apiKeyPermissionsEnabled]);

  const openPermissionsPanel = useCallback(() => {
    permissionsPanelSnapshot.current = {
      permissions: copyAPIKeyPermissions({
        bankAccountLevelOverrides: getValues('permissions.bankAccountLevelOverrides') ?? [],
        entityLevelOverrides: getValues('permissions.entityLevelOverrides') ?? [],
        platformLevelPermissions:
          getValues('permissions.platformLevelPermissions') ??
          createDefaultAPIKeyPermissions().platformLevelPermissions,
      }),
      preset: selectedPermissionPreset,
    };
    setIsPermissionsPanelOpen(true);
  }, [getValues, selectedPermissionPreset]);

  const closePermissionsPanel = useCallback(() => {
    setIsPermissionsPanelOpen(false);
    permissionsPanelSnapshot.current = null;
  }, []);

  const submitPermissionsPanel = useCallback(() => {
    setSelectedPermissionPreset(
      getAPIKeyPermissionPresetFromPermissions(sanitizeAPIKeyPermissions(getValues('permissions')))
    );
    closePermissionsPanel();
  }, [closePermissionsPanel, getValues]);

  const cancelPermissionsPanel = useCallback(() => {
    const snapshot = permissionsPanelSnapshot.current;

    if (snapshot) {
      setSelectedPermissionPreset(snapshot.preset);
      setValue('permissions.bankAccountLevelOverrides', snapshot.permissions.bankAccountLevelOverrides, {
        shouldDirty: true,
      });
      setValue('permissions.entityLevelOverrides', snapshot.permissions.entityLevelOverrides, { shouldDirty: true });
      setValue('permissions.platformLevelPermissions', snapshot.permissions.platformLevelPermissions, {
        shouldDirty: true,
      });
    }

    closePermissionsPanel();
  }, [closePermissionsPanel, setValue]);

  const handlePermissionPresetChange = useCallback(
    (preset: APIKeyPermissionPreset) => {
      setSelectedPermissionPreset(preset);

      if (preset === APIKeyPermissionPreset.Custom) {
        setValue(
          'permissions.platformLevelPermissions',
          getValues('permissions.platformLevelPermissions') ??
            createDefaultAPIKeyPermissions().platformLevelPermissions,
          { shouldDirty: true, shouldTouch: true }
        );
        openPermissionsPanel();
        return;
      }

      const permissionLevel = preset === APIKeyPermissionPreset.ViewOnly ? 'read' : 'write';
      const presetPermissions = createPresetAPIKeyPermissions(permissionLevel, platformId);

      setIsPermissionsPanelOpen(false);
      setValue('permissions.bankAccountLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.entityLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.platformLevelPermissions', presetPermissions.permissions.platformLevelPermissions, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, openPermissionsPanel, platformId, setValue]
  );

  useBreadcrumbTitle(id ? apiKeyName : 'Create');

  useEffect(() => {
    if (!id) {
      setSelectedPermissionPreset(APIKeyPermissionPreset.FullAccess);
      setSavedAPIKeySaveValues(null);
      reset(defaultFormValues(platformId));
      return;
    }

    if (!apiKeyQuery.data) {
      return;
    }

    const response = apiKeyQuery.data;
    const responsePermissions = response.permissions
      ? normalizeAPIKeyPermissions(response.permissions)
      : createDefaultAPIKeyPermissions();
    const nextFormValues: APIKeyFormValues = {
      ...defaultFormValues(platformId),
      apiKeyToken: response.api_key_token,
      description: response.description,
      name: response.name,
      permissions: responsePermissions,
    };
    const nextPermissionPreset = response.permissions
      ? getAPIKeyPermissionPresetFromPermissions(responsePermissions)
      : APIKeyPermissionPreset.FullAccess;

    setApiKeyName(response.name);
    setSelectedPermissionPreset(nextPermissionPreset);
    setSavedAPIKeySaveValues(getAPIKeySaveValues(nextFormValues));
    reset(nextFormValues);
  }, [apiKeyQuery.data, id, platformId, reset]);

  useEffect(() => {
    if (!id || !apiKeyQuery.error) {
      return;
    }

    navigate(ROUTE.PLATFORM_API_KEYS);
    if (getAPIErrorCode(apiKeyQuery.error) === 'invalid_resource_id_format') {
      addDangerNotification({
        content: 'API key not found',
      });
      return;
    }

    addDangerNotification({
      content: getErrorMessage(apiKeyQuery.error),
    });
  }, [addDangerNotification, apiKeyQuery.error, id, navigate]);

  const hasUnsavedChanges = useMemo(() => {
    if (!id) {
      return true;
    }

    if (!savedAPIKeySaveValues) {
      return false;
    }

    return (
      serializeAPIKeySaveValues(getAPIKeySaveValues(formValues)) !== serializeAPIKeySaveValues(savedAPIKeySaveValues)
    );
  }, [formValues, id, savedAPIKeySaveValues]);

  const handleSave = (data: APIKeyFormValues) => {
    if (!currentUser) {
      return;
    }

    const saveValues = getAPIKeySaveValues(data);
    const permissionSavePayload = getPermissionSavePayload(selectedPermissionPreset, saveValues.permissions);
    const permissions = apiKeyPermissionsEnabled ? permissionSavePayload : undefined;

    if (id) {
      const updateBody: UpdateApiKeyRequest = {
        description: saveValues.description,
        name: saveValues.name,
        ...(typeof permissions !== 'undefined' && permissions !== null ? { permissions } : {}),
      };
      const updateRequestBody = permissions === null ? { ...updateBody, permissions: null } : updateBody;

      updateAPIKey
        .mutateAsync({
          body: updateRequestBody as UpdateApiKeyRequest,
          path: { api_key_id: id, platform_id: saveValues.platformId },
        })
        .then(() => {
          setApiKeyName(saveValues.name);
          setSavedAPIKeySaveValues(saveValues);
          reset(data);
          queryClient.invalidateQueries({
            queryKey: apikeyGetApiKeyQueryKey({
              path: { api_key_id: id, platform_id: saveValues.platformId },
            }),
          });
          queryClient.invalidateQueries({
            queryKey: apikeyListApiKeysQueryKey({
              path: { platform_id: saveValues.platformId },
            }),
          });
          addSuccessNotification({
            content: 'API key updated',
          });
        })
        .catch((error) => {
          addDangerNotification({
            content: getErrorMessage(error),
          });
        });
      return;
    }

    const createBody = {
      description: saveValues.description,
      name: saveValues.name,
      platform_id: saveValues.platformId,
    };
    const createRequestBody: CreateApiKeyRequest =
      permissions !== null && typeof permissions !== 'undefined' ? { ...createBody, permissions } : createBody;

    createAPIKey
      .mutateAsync({
        body: createRequestBody,
      })
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: apikeyListApiKeysQueryKey({
            path: { platform_id: saveValues.platformId },
          }),
        });
        navigate(`${ROUTE.PLATFORM_API_KEYS}/edit/${response.id}`);
        addSuccessNotification({
          content: 'API key created',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: getErrorMessage(error),
        });
      });
  };
  const handleCancel = useCallback(() => {
    if (id) {
      navigate(ROUTE.PLATFORM_API_KEYS);
      return;
    }
    navigate(-1);
  }, [id, navigate]);
  const isEditLoading = !!id && apiKeyQuery.isLoading;
  const canWriteAPIKeys = isAuthorized({ permission: 'apiKeys', permissionLevel: 'write' });

  const deleteAction = id ? (
    <Button
      isDisabled={!canWriteAPIKeys}
      onClick={() => openDeleteAPIKeyAlert({ id, name: apiKeyName })}
      size="small"
      variant="secondary-danger"
    >
      Delete API Key
    </Button>
  ) : null;
  const backAction = (
    <Button onClick={handleCancel} variant="secondary" size="small">
      Cancel
    </Button>
  );
  const saveAction = (
    <Button onClick={handleSubmit(handleSave)} isDisabled={!canWriteAPIKeys} size="small" icon={!id && <Icon.Plus />}>
      {id ? 'Save API Key' : 'Create API Key'}
    </Button>
  );
  const headerDecoration = currentPlatform ? (
    <Badge
      variant="outline"
      className={cn(
        'ml-1 translate-y-px rounded-md',
        isSandbox
          ? 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300'
          : 'border-seafoam-700/10 bg-seafoam-100/50 text-seafoam-700 dark:border-seafoam-400/20 dark:bg-seafoam-500/15 dark:text-seafoam-300'
      )}
    >
      {isSandbox ? 'Sandbox' : 'Live'}
    </Badge>
  ) : null;

  const actions =
    id && !hasUnsavedChanges ? (
      <PageActions key="api-key-back-action" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
      </PageActions>
    ) : (
      <PageActions key="api-key-save-actions" headerDecoration={headerDecoration}>
        {deleteAction}
        {backAction}
        {saveAction}
      </PageActions>
    );

  return (
    <>
      {actions}
      <Loading isLoading={isEditLoading}>
        {!canWriteAPIKeys && (
          <Inner pb={0}>
            <RestrictedBanner />
          </Inner>
        )}
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(handleSave)}>
            {RenderFields<APIKeyFormValues>({
              sections: [
                {
                  fields: [
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Input
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Name"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Textarea
                            value={value !== undefined ? String(value) : ''}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Description"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    ...(apiKeyPermissionsEnabled
                      ? [
                          {
                            id: 'permissions.platformLevelPermissions' as const,
                            label: 'Permissions',
                            children: () => (
                              <>
                                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                  <Dropdown
                                    active={selectedPermissionPreset}
                                    options={apiKeyPermissionPresetOptions}
                                    onChange={(preset) =>
                                      handlePermissionPresetChange(preset as APIKeyPermissionPreset)
                                    }
                                    fullWidth
                                  />
                                  {selectedPermissionPreset === APIKeyPermissionPreset.Custom && (
                                    <Button
                                      className="h-10"
                                      type="button"
                                      variant="secondary"
                                      size="small"
                                      icon={<Icon.Edit />}
                                      onClick={openPermissionsPanel}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </div>
                              </>
                            ),
                          },
                        ]
                      : []),
                  ],
                },
                {
                  fields: [
                    {
                      id: 'apiKeyToken',
                      label: 'API Key',
                      children: ({ value }) => {
                        return (
                          <ApiKeyInput
                            value={value === undefined ? '' : String(value)}
                            placeholder="Key Will be Generated"
                          />
                        );
                      },
                    },
                  ],
                },
              ],
            })}
            {apiKeyPermissionsEnabled && (
              <PopoverBase
                show={isPermissionsPanelOpen}
                usePortal
                withoutBackdrop
                className={cn(
                  '![--animation-enter-x:24px] ![--animation-exit-x:24px] ![--animation-enter-y:0px] ![--animation-exit-y:0px]',
                  '[--popover-left:-24px] [--popover-padding:0] [--popover-position:fixed] [--popover-right:0] [--popover-top:0px] [--popover-width:min(100vw,700px)]',
                  'fixed! top-0! right-0! bottom-0! left-[-24px]! z-[14]! flex! h-dvh! w-[calc(100vw+24px)]! justify-end! overflow-hidden! bg-[rgba(18,18,22,0.56)]',
                  '[&>div]:h-dvh! [&>div]:w-[min(100vw,700px)]! [&>div]:rounded-none! [&>div]:flex [&>div]:flex-col',
                  '[&>div]:shadow-[1px_0_0_var(--color-border),-1px_0_0_var(--color-border),0_4px_16px_rgba(38,38,44,0.03)]!'
                )}
              >
                <div className="flex items-center gap-4 border-b border-gray-200 bg-background px-5 py-4">
                  <h4 className="m-0 flex-auto text-base font-medium leading-5 text-foreground">Permissions</h4>
                  <Button
                    type="button"
                    variant="subtle"
                    size="small"
                    icon={<Icon.Cross />}
                    onClick={cancelPermissionsPanel}
                  />
                </div>
                <Tabs
                  value={visibleSection}
                  onValueChange={(section) => setVisibleSection(section as Section)}
                  className="px-3 py-0.5 shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-950)_8%,transparent)] bg-gray-25 z-10"
                >
                  <TabsList variant="ghost" className="gap-0.5">
                    {apiKeySections.map((section) => (
                      <TabsTrigger key={section.value} value={section.value}>
                        {section.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <div className="flex-auto overflow-y-auto [--page-margin:24px]">
                  <PermissionConfiguration
                    visibleSection={
                      visibleSection === Section.Transfers
                        ? PermissionConfigurationSection.Transfers
                        : PermissionConfigurationSection.Additional
                    }
                    excludeKeys={[...excludedAPIKeyPermissionKeys]}
                    fieldPathPrefix="permissions"
                    forcedMode={selectedMode}
                    includeAdditionalPermissionKeys={[...includedAPIKeyAdditionalPermissionKeys]}
                    onOffEnabledPermissionLevel="read"
                    sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
                    liveEntitiesAndAccounts={liveEntitiesAndAccounts}
                  />
                </div>
                <div className="box-border flex justify-between gap-5 border-t border-gray-200 bg-gray-25 px-6 py-5">
                  <Button type="button" variant="secondary" onClick={cancelPermissionsPanel}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={submitPermissionsPanel}>
                    Done
                  </Button>
                </div>
              </PopoverBase>
            )}
          </form>
        </FormProvider>
      </Loading>
    </>
  );
};
